import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Send, ShieldCheck, FileText, ArrowLeft, AlertCircle, Gavel, Star, Check, Flag } from "lucide-react";
import { toast } from "sonner";
import DisputeDialog from "@/components/DisputeDialog";

export default function BookingDetailPage() {
    const { bookingId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [booking, setBooking] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [invoicePrice, setInvoicePrice] = useState("");
    const [invoiceNotes, setInvoiceNotes] = useState("");
    const [showInvoiceForm, setShowInvoiceForm] = useState(false);
    const [disputeOpen, setDisputeOpen] = useState(false);
    const messagesEndRef = useRef(null);

    const isProvider = user?.role === "provider" || user?.role === "admin";

    const refresh = () => {
        api.get(`/bookings/${bookingId}`).then((r) => setBooking(r.data)).catch(() => {});
        api.get(`/bookings/${bookingId}/messages`).then((r) => setMessages(r.data)).catch(() => {});
    };

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 6000);
        return () => clearInterval(t);
    }, [bookingId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length]);

    const send = async (e) => {
        e.preventDefault();
        if (!draft.trim()) return;
        setSending(true);
        try {
            await api.post(`/bookings/${bookingId}/messages`, { content: draft.trim() });
            setDraft("");
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setSending(false);
        }
    };

    const sendInvoice = async () => {
        if (!invoicePrice) {
            toast.error("Enter a final price");
            return;
        }
        try {
            await api.post(`/bookings/${bookingId}/invoice`, {
                final_price: parseFloat(invoicePrice),
                invoice_notes: invoiceNotes,
            });
            toast.success("Invoice sent to client.");
            setShowInvoiceForm(false);
            setInvoicePrice("");
            setInvoiceNotes("");
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const safetyPing = async () => {
        try {
            await api.post(`/bookings/${bookingId}/safety-ping`);
            toast.success("Safety check confirmed ✓");
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    if (!booking) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="max-w-7xl mx-auto px-6 py-32 text-muted-foreground overline">Loading...</div>
            </div>
        );
    }

    const depositPaid = booking.payment_status === "paid";
    const isAssignedProvider = isProvider && booking.provider_id;
    const isInProgress = booking.status === "in_progress";

    return (
        <div className="min-h-screen bg-background" data-testid="booking-detail-page">
            <Header />

            <section className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
                <button
                    onClick={() => navigate(-1)}
                    className="overline text-muted-foreground hover:text-foreground mb-8 flex items-center gap-2"
                    data-testid="booking-detail-back"
                >
                    <ArrowLeft size={12} /> Back
                </button>

                <div className="grid lg:grid-cols-12 gap-10">
                    {/* Booking summary */}
                    <aside className="lg:col-span-4 space-y-6">
                        <div className="bg-card border border-white/10 rounded-sm p-6">
                            <div className="overline mb-2">{booking.service_category}</div>
                            <h1 className="font-serif text-2xl leading-tight">{booking.service_name}</h1>
                            <div className="mt-4 flex flex-wrap gap-2">
                                <Badge className="bg-secondary text-foreground border border-white/10 uppercase text-[10px] tracking-[0.2em]">
                                    {booking.status.replace("_", " ")}
                                </Badge>
                                <Badge
                                    variant="outline"
                                    className={`uppercase text-[10px] tracking-[0.2em] border ${
                                        depositPaid ? "border-emerald-700/40 text-emerald-300" : "border-white/10 text-muted-foreground"
                                    }`}
                                    data-testid="deposit-badge"
                                >
                                    deposit {booking.payment_status}
                                </Badge>
                            </div>
                            <div className="hairline my-5" />
                            <Row label="Schedule" value={format(parseISO(booking.scheduled_at), "MMM d · HH:mm")} />
                            <Row label="Where" value={`${booking.address}, ${booking.city}`} />
                            {booking.distance_km != null && booking.eta_minutes != null && (
                                <Row
                                    label="Distance / ETA"
                                    value={
                                        <span data-testid="detail-eta">
                                            <span className="gold-text">{booking.distance_km}km</span>
                                            <span className="text-muted-foreground"> · </span>
                                            <span className="gold-text">{booking.eta_minutes} min drive</span>
                                        </span>
                                    }
                                />
                            )}
                            {booking.provider_name && <Row label="Pro" value={booking.provider_name} />}
                            <div className="hairline my-5" />
                            <Row label="Deposit" value={`$${(booking.deposit_amount || booking.total_amount).toLocaleString()}`} />
                            {booking.final_price && (
                                <>
                                    <Row label="Final price" value={`$${booking.final_price.toLocaleString()}`} />
                                    <Row
                                        label="Balance owed"
                                        value={
                                            booking.balance_charged
                                                ? "Charged ✓"
                                                : `$${(booking.balance_due || 0).toLocaleString()}`
                                        }
                                    />
                                </>
                            )}
                            {booking.transport_fee > 0 && <Row label="Transport" value={`$${booking.transport_fee.toLocaleString()}`} />}
                            {booking.wait_minutes > 0 && (
                                <Row label="Wait time" value={`${booking.wait_minutes} min · $${booking.wait_charge.toLocaleString()}`} />
                            )}
                            <div className="hairline my-5" />
                            <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground/70 text-center" data-testid="booking-llc-line">
                                Invoiced by SMART DEMANDS 24/7 LIMITED · A company duly incorporated under the Laws of Jamaica
                            </div>
                        </div>

                        {/* Provider actions */}
                        {isAssignedProvider && (
                            <div className="bg-card border border-primary/30 rounded-sm p-6 space-y-4" data-testid="provider-actions">
                                <div className="overline">Provider Actions</div>
                                {isInProgress && (
                                    <Button
                                        onClick={safetyPing}
                                        className="w-full bg-secondary text-foreground hover:bg-secondary/80 rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                                        data-testid="safety-ping-btn"
                                    >
                                        <ShieldCheck size={14} className="mr-2" /> I'm safe — confirm
                                    </Button>
                                )}
                                {!showInvoiceForm ? (
                                    <Button
                                        onClick={() => setShowInvoiceForm(true)}
                                        className="w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                                        data-testid="open-invoice-btn"
                                    >
                                        <FileText size={14} className="mr-2" /> {booking.final_price ? "Update invoice" : "Send invoice"}
                                    </Button>
                                ) : (
                                    <div className="space-y-3" data-testid="invoice-form">
                                        <div>
                                            <div className="overline mb-1">Final price (JMD)</div>
                                            <Input
                                                type="number"
                                                value={invoicePrice}
                                                onChange={(e) => setInvoicePrice(e.target.value)}
                                                placeholder="e.g. 8500"
                                                className="bg-input border-white/10 h-11 rounded-sm"
                                                data-testid="invoice-price-input"
                                            />
                                        </div>
                                        <div>
                                            <div className="overline mb-1">Notes</div>
                                            <Textarea
                                                value={invoiceNotes}
                                                onChange={(e) => setInvoiceNotes(e.target.value)}
                                                rows={3}
                                                placeholder="Itemised charges or scope notes"
                                                className="bg-input border-white/10 rounded-sm"
                                                data-testid="invoice-notes-input"
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={sendInvoice}
                                                className="flex-1 bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-10"
                                                data-testid="invoice-send-btn"
                                            >
                                                Send to client
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                onClick={() => setShowInvoiceForm(false)}
                                                className="rounded-sm uppercase tracking-[0.2em] text-xs h-10 text-muted-foreground"
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {!depositPaid && (
                            <div className="flex items-start gap-2 p-4 bg-amber-900/15 border border-amber-700/30 text-amber-200 text-xs rounded-sm">
                                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                                <div>
                                    Phone numbers will be hidden in chat until your deposit clears. SMART never
                                    permits cash exchange — final balance is charged to your card on completion.
                                </div>
                            </div>
                        )}

                        {/* Dispute / Report issue — available after a booking has started */}
                        {["in_progress", "completed", "cancelled"].includes(booking.status) && (
                            <button
                                data-testid="open-dispute-btn"
                                onClick={() => setDisputeOpen(true)}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-sm uppercase tracking-[0.2em] text-xs border transition-colors ${
                                    booking.has_open_dispute
                                        ? "border-amber-700/40 text-amber-300 hover:bg-amber-500/5"
                                        : "border-red-700/40 text-red-300 hover:bg-red-500/5"
                                }`}
                            >
                                <Flag size={13} />
                                {booking.has_open_dispute ? "View open dispute" : "Report an issue"}
                            </button>
                        )}
                    </aside>

                    {/* Chat */}
                    <div className="lg:col-span-8 flex flex-col bg-card border border-white/10 rounded-sm overflow-hidden h-[70vh]">
                        <div className="p-4 border-b border-white/10">
                            <div className="overline">Conversation</div>
                            <div className="text-xs text-muted-foreground mt-1">
                                Discuss scope and finalise pricing here. The provider will send a formal invoice when ready.
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                            {messages.length === 0 ? (
                                <div className="text-center text-muted-foreground text-sm py-12">
                                    No messages yet. Start the conversation below.
                                </div>
                            ) : (
                                messages.map((m) => {
                                    const mine = m.sender_id === user?.id;
                                    return (
                                        <div
                                            key={m.id}
                                            className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                            data-testid={`message-${m.id}`}
                                        >
                                            <div
                                                className={`max-w-[75%] px-4 py-2.5 rounded-sm text-sm ${
                                                    mine
                                                        ? "bg-primary text-primary-foreground"
                                                        : "bg-secondary text-foreground border border-white/10"
                                                }`}
                                            >
                                                <div className="text-[10px] uppercase tracking-[0.2em] opacity-70 mb-1">
                                                    {mine ? "You" : `${m.sender_name} · ${m.sender_role}`}
                                                </div>
                                                <div className="whitespace-pre-wrap leading-snug">{m.content}</div>
                                                <div className="text-[10px] opacity-50 mt-1">
                                                    {format(parseISO(m.created_at), "MMM d · HH:mm")}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2" data-testid="message-form">
                            <Input
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder="Type a message..."
                                className="bg-input border-white/10 h-11 rounded-sm flex-1"
                                data-testid="message-input"
                            />
                            <Button
                                type="submit"
                                disabled={sending || !draft.trim()}
                                className="bg-primary text-primary-foreground rounded-sm h-11 px-5"
                                data-testid="message-send-btn"
                            >
                                <Send size={14} />
                            </Button>
                        </form>
                    </div>
                </div>
            </section>

            {/* Bids Received panel — only when booking is open_for_bids and viewer is the customer */}
            {booking.status === "open_for_bids" && booking.customer_id === user?.id && (
                <BidsReceivedPanel
                    bookingId={booking.id}
                    onAccepted={() => window.location.reload()}
                />
            )}

            <DisputeDialog
                bookingId={booking.id}
                open={disputeOpen}
                onClose={() => { setDisputeOpen(false); refresh(); }}
            />

            <Footer />
        </div>
    );
}

function BidsReceivedPanel({ bookingId, onAccepted }) {
    const [bids, setBids] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acceptingId, setAcceptingId] = useState(null);

    const refresh = () => {
        setLoading(true);
        api
            .get(`/bookings/${bookingId}/bids`)
            .then((r) => setBids(r.data || []))
            .catch(() => setBids([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 8000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingId]);

    const accept = async (bidId) => {
        if (!window.confirm("Accept this Royal bid? This will lock in the pro and confirm your booking.")) return;
        setAcceptingId(bidId);
        try {
            await api.post(`/bids/${bidId}/accept`);
            toast.success("Bid accepted — your Royal pro is locked in.");
            onAccepted?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setAcceptingId(null);
        }
    };

    return (
        <section className="px-6 lg:px-12 pb-16" data-testid="bids-received-panel">
            <div className="max-w-5xl mx-auto bg-card border border-primary/30 p-7 lg:p-9 rounded-sm relative overflow-hidden">
                <div className="absolute -top-24 -right-20 w-64 h-64 rounded-full bg-primary/8 blur-3xl pointer-events-none" />
                <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                        <Gavel size={14} className="gold-text" strokeWidth={1.5} />
                        <div className="overline">
                            Royal Bids · {bids.length} received {loading ? "· refreshing" : ""}
                        </div>
                    </div>
                    <h3 className="font-serif text-2xl sm:text-3xl mb-6">
                        Pros are bidding. <span className="italic gold-grad">Pick your fit.</span>
                    </h3>

                    {bids.length === 0 ? (
                        <div className="text-sm text-muted-foreground">
                            No bids yet — qualifying pros are being notified. Bids usually arrive within 30–60 minutes.
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {bids.map((b) => (
                                <li
                                    key={b.id}
                                    className="flex flex-wrap items-center gap-4 p-5 bg-background/60 border border-white/10 rounded-sm hover:border-primary/40 transition-colors"
                                    data-testid={`bid-row-${b.id}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-base font-medium text-foreground">
                                                {b.provider_name}
                                            </span>
                                            {b.provider_rating > 0 && (
                                                <span className="text-[11px] text-amber-300 flex items-center gap-0.5">
                                                    <Star size={10} strokeWidth={1.5} fill="currentColor" /> {b.provider_rating.toFixed(1)}
                                                </span>
                                            )}
                                            {b.provider_verification_level === "royal" && (
                                                <span className="text-[9px] uppercase tracking-[0.25em] gold-text border border-primary/40 px-1.5 py-0.5">
                                                    Royal
                                                </span>
                                            )}
                                            {b.provider_verification_level === "provisional" && (
                                                <span className="text-[9px] uppercase tracking-[0.25em] text-amber-300 border border-amber-500/40 px-1.5 py-0.5">
                                                    Provisional
                                                </span>
                                            )}
                                        </div>
                                        {b.message && (
                                            <p className="mt-1.5 text-xs text-muted-foreground italic leading-relaxed">
                                                "{b.message}"
                                            </p>
                                        )}
                                        <div className="mt-1.5 text-[11px] text-muted-foreground space-x-3">
                                            {b.hourly_rate ? <span>${b.hourly_rate.toLocaleString()}/hr</span> : null}
                                            {b.eta_minutes ? <span>{b.eta_minutes}min ETA</span> : null}
                                            {b.counter_count > 0 && <span className="text-amber-300">Counter ×{b.counter_count}</span>}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-serif text-2xl gold-text">
                                            ${Math.round(b.price).toLocaleString()}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">JMD total</div>
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={() => accept(b.id)}
                                        disabled={acceptingId === b.id}
                                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-[10px] h-10 px-5"
                                        data-testid={`accept-bid-${b.id}`}
                                    >
                                        <Check size={12} className="mr-1" strokeWidth={2} />
                                        {acceptingId === b.id ? "Locking..." : "Accept"}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-baseline justify-between gap-3 py-1 text-sm">
            <div className="overline">{label}</div>
            <div className="text-foreground text-right">{value}</div>
        </div>
    );
}
